import StarsBanner from "~/ui/stars-banner";
import FirmLike from "./like";
import { loadClamp } from "~/es6/clamp-loader";
import { HIDE_CLASS } from "./const";
import FirmReviewRemove from "./remove";
import { store } from "~/state/store";
import { node, icon } from "~/util/dom";
import { deferPromise } from "~/util/general";
import { createVerifiedUserTooltip } from "~/util/userweb";
import { CONFIG } from "~/config";
import { dateFormat } from "~/util/format";
import { getFirmLayout, clamp, getFirmImpressConfig, hasFilledStructuredReviews } from "~/util/review";
import { createReviewPhotos } from "~/ui/review/photos";
import { createEmojisPreview } from "~/ui/review/emojisPreview";
import { IReview, IReviewItem } from "./interfaces";
import { IDeferPromise, IReviewConf } from "~/interfaces";
import { _ } from "~/i18n";
import ReviewPopup from "./popupwc";

const OPEN_CLASS = "open";

interface IFirmReviewEdit {
	data: IReviewConf;
	isBottom?: boolean;
	isPanel?: boolean;
	logSource?: string;
	analytics?: {[key: string]: string|number|boolean};
	inactive?: boolean;
	thankYouCont?: null | HTMLElement;
	update?: boolean;
	onSave?: (data: { review: IReview; myReview: IReviewItem; }) => void;
	onCancel?: () => void;
	poi?: {
		source: string;
		id: string | number;
		title: string;
		poitypeId: number;
		mark: {
			lon: number;
			lat: number;
		}
	};
}

// editace - bud zobrazime content pro vypis nebo editaci
export default class FirmReviewEdit {
	_dom: {
		[key: string]: HTMLElement;
	};

	_opts: IFirmReviewEdit;

	_myReview: IReviewItem;

	_putReviewId: number | string;

	_logPosition: string;

	_firstStarsClick: boolean;

	_firmLike: FirmLike;

	_firmImpressWasSend: boolean;

	_clampIsShortened: boolean;

	_firstClampPromiseData: IDeferPromise<any>;

	constructor(optsArg: IFirmReviewEdit) {
		this._dom = {
			container: node("div", { className: "firm-review-edit" }),
		};
		this._opts = {
			data: null,
			isBottom: false, // pozice, default top
			isPanel: false, // jedna se o panel?
			logSource: "recenze-poi",
			analytics: {},
			inactive: false,
			thankYouCont: null, // container, do ktereho se ma thank you pripnout
			update: false, // jedna se o editaci, nebo aktualizaci
			onSave: () => { /**/ }, // putReviewData data
			onCancel: () => { /**/ },
			...optsArg,
		};
		this._myReview = null;
		this._putReviewId = this._opts.data.id;
		this._logPosition = this._opts.isBottom ? "bottom" : "top";
		this._firstStarsClick = true;
		this._firmLike = null;
		this._firmImpressWasSend = false;
		this._clampIsShortened = false;
		this._firstClampPromiseData = deferPromise();

		this._build();
		this.setMyReview(this._opts.data.myReview);
	}

	destroy() {
		this._dom.container.remove();
	}

	get container() {
		return this._dom.container;
	}

	get header() {
		return this._dom.header;
	}

	openEdit() {
		this._openEdit();
	}

	setMyReview(data: IReviewItem) {
		this._myReview = data ? { ...data } : null;

		this._update();
	}

	handleEvent(event: Event) {
		if (event.type === "pointerdown") {
			if (event.target !== this._dom.verticalIcon && !this._dom.optsList.contains(event.target as HTMLElement)) {
				this._closeOpts();
			}
		}
	}

	firmImpressAnalytics() {
		if (this._firmImpressWasSend || !this._myReview) {
			return true;
		}

		this._firmImpressAnalytics();
		this._firmImpressWasSend = true;

		return false;
	}

	_firmImpressAnalytics(reviewShowMoreAction = false) {
		this._firstClampPromiseData.promise.then(() => {
			const config = getFirmImpressConfig({
				reviewId: this._putReviewId,
				state: this._myReview.state,
				reviewOrder: 1,
				reviewShortened: reviewShowMoreAction || this._clampIsShortened,
				reviewAuthor: true,
				reviewShowMoreAction,
			});

			Mapy.getComponent("logger").log(config.action, config.data);
		}, () => { /**/ });
	}

	_build() {
		// hodnceni
		const reviewCont = node("div", { className: "review-cont" });
		const header = node("a", { className: "header-part", href: `https://${CONFIG.BASE_URL}/?moje-mapy&cat=reviews` });
		const { user } = store.getState().user;
		const imgHolder = node("mapy-avatar", { className: "img-holder", size: 40, premium: user.premium ? "mapy" : "none" });

		if (user.avatar) {
			const userBadge = node("img", { src: user.avatar });

			userBadge.crossOrigin = "anonymous";
			imgHolder.appendChild(userBadge);
		} else {
			imgHolder.classList.add("svg-avatar");
			imgHolder.appendChild(icon("avatar-anonym"));
		}

		header.appendChild(imgHolder);
		const infoPart = node("div", { className: "info-part" });

		header.appendChild(infoPart);
		reviewCont.appendChild(header);
		const title = node("h2");

		title.appendChild(node("div", {}, (user.name || user.accountDisplayName.replace(/[@].*$/u, "")).trim()));

		if (user.bankId) {
			const verifiedIcon = icon("verifieduser16");

			title.appendChild(verifiedIcon);
			createVerifiedUserTooltip(verifiedIcon);
		}

		infoPart.appendChild(title);
		// stars count
		const ratingStars = node("span", { className: "stars-placeholder" });
		// date
		const date = node("span", { className: "date" });
		const allReviews = node("p", { className: `all-reviews`, textContent: _("detail.content.booking.reviewCount2", { count: this._opts?.data?.myReview?.user?.review_count || 0 }) });

		allReviews.appendChild(icon("forward"));
		infoPart.appendChild(allReviews);
		// hodnoceni a datum
		const starsWithDate = node("div", { className: "stars-with-date" });

		starsWithDate.appendChild(ratingStars);
		starsWithDate.appendChild(date);
		reviewCont.appendChild(starsWithDate);
		const optsArea = node("span", { className: "opts-area" });
		const verticalIcon = icon("etc-vertical");
		const optsList = node("span", { className: "list" });
		const editBtn = node("button", { className: "list-edit-btn" });

		editBtn.setAttribute("data-log-ignore", "");
		editBtn.appendChild(icon("edit"));
		const editBtnSpan = node("span");

		editBtn.appendChild(editBtnSpan);
		const removeBtn = node("button", { className: "list-remove-btn" });

		removeBtn.setAttribute("data-log-ignore", "");
		removeBtn.appendChild(icon("delete"));
		removeBtn.appendChild(node("span", { textContent: _("delete") }));

		optsArea.appendChild(verticalIcon);
		optsArea.appendChild(optsList);
		editBtn.addEventListener("click", () => {
			this._closeOpts();
			// analytika
			this._log("click", {
				text: this._myReview.actual === false ? "Aktualizovat hodnocení" : "Upravit hodnocení",
			});
			this._openEdit();
		});
		removeBtn.addEventListener("click", () => {
			this._closeOpts();
			// analytika
			this._log("click", {
				text: "Smazat",
			});

			this._openRemovePopup();
		});
		verticalIcon.addEventListener("click", event => {
			event.preventDefault();
			event.stopPropagation();
			// analytika
			this._log("click", {
				text: "contextMenu",
				section: "reviews",
			});
			optsArea.classList.toggle(OPEN_CLASS);

			if (optsArea.classList.contains(OPEN_CLASS)) {
				document.addEventListener("pointerdown", this);
			} else {
				this._closeOpts();
			}
		});

		header.addEventListener("click", event => {
			if (Mapy.Util.linkToNewWindow(event)) {
				return;
			}

			event.preventDefault();

			if (!optsList.contains(event.target as HTMLElement)) {
				Mapy.getComponent("mymaps").deactivate();
				Mapy.getComponent("mymaps").activate();
				Mapy.getComponent("mymaps").activeComponent(Mapy.getComponent("reviews"));

				setTimeout(() => {
					Mapy.getComponent("history").save();
				}, 0);
			}
		});

		if (!this._opts.inactive) {
			infoPart.appendChild(optsArea);
		}

		const myReviewWeight = node("p", { style: "font-style: italic; color: gray; display: none" });
		const reviewDesc = node("p");
		// strukturovane hodnoceni
		const strRatingCont = node("div", { className: "str-rating-cont" });
		// fotky
		const photosCont = node("div", { className: "photos-cont" });
		// like vlastni recenze
		const likeCont = node("div", { className: "like-cont" });

		reviewCont.append(
			myReviewWeight,
			reviewDesc,
			strRatingCont,
			photosCont,
			likeCont,
		);

		const rejectedCont = node("div", { className: "rejected-cont" });

		this._dom.container.append(
			reviewCont,
			rejectedCont,
		);
		Object.assign(this._dom, {
			reviewCont,
			reviewDesc,
			ratingStars,
			date,
			myReviewWeight,
			optsArea,
			verticalIcon,
			optsList,
			editBtnSpan,
			likeCont,
			header,
			rejectedCont,
			strRatingCont,
			photosCont,
			editBtn,
			removeBtn,
		});
	}

	_update() {
		this._dom.editBtnSpan.textContent = this._myReview?.actual === false ? _("update") : _("edit");
		this._dom.editBtn.remove();
		this._dom.removeBtn.remove();

		if (!this._myReview) {
			return;
		}

		const date = new Date(this._myReview.updated);
		const createDate = dateFormat(date, "d. mn yyyy");

		this._dom.reviewDesc.innerText = this._myReview.text || "";
		this._dom.date.textContent = createDate;
		this._dom.strRatingCont.innerHTML = "";
		this._dom.photosCont.innerHTML = "";
		this._dom.rejectedCont.innerHTML = "";

		if (Mapy.CUS.userweb && Mapy.CUS.userweb.firmDebug && typeof this._myReview.weight === "number") {
			this._dom.myReviewWeight.innerHTML = `Celková váha: <strong>${(this._myReview.weight || 0).toFixed(3)}`;
			this._dom.myReviewWeight.style.display = "block";
		}

		this._updateLittleStars(this._myReview.rating);

		// dom
		this._dom.reviewCont.classList.add(HIDE_CLASS);

		if (this._dom.timeCover) {
			this._dom.timeCover.remove();
			this._dom.timeCover = null;
		}

		if (this._myReview.enableEdit === true) {
			this._dom.optsList.appendChild(this._dom.editBtn);
		}

		this._dom.optsList.appendChild(this._dom.removeBtn);

		if (!this._firmLike && (this._myReview.text?.length || (Array.isArray(this._myReview.gallery) && this._myReview.gallery.length))) {
			const { data } = store.getState().detail;

			this._firmLike = new FirmLike({
				id: this._myReview.likes ? this._myReview.likes.id : `review.firm.${this._myReview.id}`,
				data: this._myReview.likes,
				disabled: true,
				detail: {
					source: data?.source,
					id: data?.id as number,
					typeId: data?.typeId,
				},
			});
			this._dom.likeCont.appendChild(this._firmLike.container);
		}

		this._dom.reviewCont.classList.remove(HIDE_CLASS);

		if (this._myReview.state === "waiting") {
			const timeCover = node("span", { className: "time-cover" });
			const timeSvg = icon("time");
			const timeCaption = node("span", { className: "time-caption", textContent: _("detail.content.review2.acceptWaiting") });

			timeCover.appendChild(timeSvg);
			timeCover.appendChild(timeCaption);
			this._dom.optsArea.insertBefore(timeCover, this._dom.optsArea.firstChild);
			this._dom.timeCover = timeCover;
		} else if (this._myReview.state === "rejected") {
			const msg = node("p", { textContent: this._myReview.rejectReason });
			const buttons = node("div", { className: "rejected-buttons" });

			if (this._myReview.enableEdit === true) {
				const editTextBtn = node("button", { className: "edit-text-btn", textContent: _("edit") });

				editTextBtn.addEventListener("click", () => {
					this._openEdit();
				});
				buttons.append(editTextBtn);
			}

			const removeReviewBtn = node("button", { className: "remove-review-btn", textContent: _("detail.content.review2.removeReview") });

			removeReviewBtn.addEventListener("click", () => {
				this._openRemovePopup();
			});
			buttons.append(removeReviewBtn);

			this._dom.rejectedCont.append(msg, buttons);
		}

		// strukturovane hodnoceni
		this._dom.strRatingCont.append(
			createEmojisPreview({
				config: this._myReview.structured_rating || [],
			}).container,
		);

		Array.isArray(this._myReview.gallery) && this._dom.photosCont.append(createReviewPhotos({
			reviewData: {
				...this._myReview,
				...this._opts.poi ? { poi: this._opts.poi } : {},
			},
		}));

		// clampneme text
		loadClamp().then(() => {
			this._clampIsShortened = clamp(this._dom.reviewDesc, true, () => {
				this._firmImpressAnalytics(true);
			});

			this._firstClampPromiseData.resolve({});
		}, () => { /**/ });
	}

	_updateLittleStars(percentage: number) {
		this._dom.ratingStars.innerHTML = "";

		const starsBanner = new StarsBanner({
			percentage,
		});

		this._dom.ratingStars.appendChild(starsBanner.container);
	}

	_openEdit() {
		// eslint-disable-next-line no-new
		new ReviewPopup({
			data: {
				...this._opts.data,
				myReview: this._myReview,
				review: {
					...this._opts.data.review,
					alternatives: [],
				},
			},
			logSource: this._opts.logSource,
			logPosition: "edit",
			fromOtherPOI: false,
			onClose: closeData => {
				if (closeData?.myReview) {
					this._myReview = closeData.myReview;
				}

				this._update();

				this._opts.onSave({
					review: closeData.review,
					myReview: closeData.myReview,
				});
			},
		});
	}

	_closeOpts() {
		this._dom.optsArea.classList.remove(OPEN_CLASS);
		document.removeEventListener("pointerdown", this);
	}

	_openRemovePopup() {
		/* eslint-disable no-new */
		new FirmReviewRemove({
			data: this._opts.data,
			myReview: this._myReview,
			logSource: this._opts.logSource,
			analytics: {
				...this._opts.analytics,
				photos: this._myReview && Array.isArray(this._myReview.gallery) && this._myReview.gallery.length > 0 ? 1 : 0,
				attributes: hasFilledStructuredReviews(this._myReview.structured_rating),
			},
			logPosition: this._logPosition,
		});
	}

	_log(name: string, data?: object) {
		/* eslint-disable camelcase */
		Mapy.getComponent("logger").log(name, {
			position: this._logPosition,
			rating_layout: getFirmLayout(this._opts.data.review, this._opts.data.booking, this._myReview, this._opts.data.isFirm),
			source: this._opts.logSource,
			refused: this._myReview?.state === "rejected" ? 1 : 0,
			type: this._myReview?.actual === false ? "recenze-update" : "recenze-edit",
			sourcePoi: this._opts.data.source,
			typeid: this._opts.data.typeId,
			firmTwin: this._opts.data.isTwin ? 1 : 0,
			section: "reviews",
			...this._opts.analytics,
			...data,
		});
	}
}
